import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, Zap, Phone, CheckCircle, AlertTriangle, Battery, Thermometer } from 'lucide-react';
import useSEO from '../../hooks/useSEO';

const SintomasBateriaDescargada = () => {
  const navigate = useNavigate();

  // SEO: Set canonical to avoid duplicate content issues
  useSEO({
    title: 'Síntomas de Batería Descargada: Cómo Identificarlos Temprano | ArrancaYa BCN',
    description: 'Aprende a reconocer los síntomas de batería descargada antes de quedarte tirado. Señales tempranas que indican problemas con la batería del coche en Barcelona.',
    canonical: 'https://arrancaya-bcn.es/blog/sintomas-bateria-descargada'
  });

  useEffect(() => {

    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = 'síntomas batería descargada, señales batería coche, diagnóstico batería, problemas batería coche Barcelona';
    document.head.appendChild(metaKeywords);

    // Schema markup
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Síntomas de Batería Descargada: Cómo Identificarlos Temprano",
      "description": "Guía completa para identificar los síntomas de una batería de coche descargada antes de quedarte tirado",
      "image": "https://images.unsplash.com/photo-1558618047-3c8c6d6c3f3a?w=800&h=400&fit=crop",
      "author": {
        "@type": "Organization",
        "name": "ArrancaYa BCN"
      },
      "publisher": {
        "@type": "Organization",
        "name": "ArrancaYa BCN",
        "logo": "https://arrancaya-bcn.es/images/logo.png"
      },
      "datePublished": "2026-01-11",
      "dateModified": "2026-01-11"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    // GTM tracking
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'page_view',
      'page_title': 'Síntomas batería descargada',
      'page_location': window.location.href,
      'content_group1': 'Blog',
      'content_group2': 'Diagnóstico'
    });

    return () => {
      document.head.removeChild(script);
      if (metaKeywords.parentNode) {
        document.head.removeChild(metaKeywords);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gray-50 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-800">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al Blog
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6">
            <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-medium">Diagnóstico</span>
            <span>•</span>
            <span>11 de enero, 2026</span>
            <span>•</span>
            <span>5 min de lectura</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 leading-tight">
            <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">Síntomas</span> de Batería Descargada
          </h1>
          
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-8 border-2 border-red-100 mb-8">
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              <strong className="text-red-600">Prevenir es mejor que lamentar</strong>. Aprende a reconocer las señales que indican 
              que tu batería está fallando antes de quedarte completamente tirado en Barcelona.
            </p>
          </div>
          
          {/* Timeline visual en lugar de imagen */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              ⏱️ Cronología: De Batería Sana a Descargada
            </h2>
            <div className="space-y-6">
              <div className="flex items-center">
                <div className="bg-green-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold mr-6">
                  ✅
                </div>
                <div className="flex-1 bg-green-50 p-4 rounded-xl border border-green-200">
                  <h3 className="font-bold text-green-800">Semanas 1-2: Batería Sana</h3>
                  <p className="text-green-700 text-sm">Arranque normal, luces brillantes, sin síntomas</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="bg-yellow-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold mr-6">
                  ⚠️
                </div>
                <div className="flex-1 bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                  <h3 className="font-bold text-yellow-800">Semanas 3-4: Primeras Señales</h3>
                  <p className="text-yellow-700 text-sm">Arranque más lento, luces menos intensas</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="bg-orange-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold mr-6">
                  🔶
                </div>
                <div className="flex-1 bg-orange-50 p-4 rounded-xl border border-orange-200">
                  <h3 className="font-bold text-orange-800">Semana 5: Síntomas Evidentes</h3>
                  <p className="text-orange-700 text-sm">Dificultad para arrancar, problemas eléctricos</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="bg-red-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold mr-6">
                  ❌
                </div>
                <div className="flex-1 bg-red-50 p-4 rounded-xl border border-red-200">
                  <h3 className="font-bold text-red-800">Semana 6: Fallo Total</h3>
                  <p className="text-red-700 text-sm">No arranca, luces muy tenues o apagadas</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="prose prose-xl max-w-none space-y-12">
          
          {/* Alerta temprana */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-yellow-800 font-semibold mb-2">⚡ Señales de Alerta Temprana</p>
                <p className="text-yellow-700 mb-0">
                  Una batería no se descarga de repente. Suele dar avisos durante semanas. 
                  Reconociendo estos síntomas puedes evitar quedarte tirado.
                </p>
              </div>
            </div>
          </div>

          <h2>🚨 Síntomas Evidentes (Batería Muy Descargada)</h2>

          <div className="grid md:grid-cols-2 gap-6 my-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-red-800 font-bold mb-3">❌ Síntomas Críticos:</h3>
              <ul className="text-red-700 space-y-2">
                <li>• <strong>No arranca</strong> - Solo se escucha "clic"</li>
                <li>• <strong>Luces muy tenues</strong> o que no encienden</li>
                <li>• <strong>Radio/navegador se apaga</strong> al arrancar</li>
                <li>• <strong>Motor gira muy lento</strong> al intentar arrancar</li>
                <li>• <strong>Luces del tablero parpadean</strong></li>
              </ul>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <h3 className="text-orange-800 font-bold mb-3">⚠️ Acción Inmediata:</h3>
              <ul className="text-orange-700 space-y-2">
                <li>• <strong>No sigas intentando</strong> arrancar</li>
                <li>• Busca cables de arranque</li>
                <li>• Llama a ArrancaYa BCN: 613 076 099</li>
                <li>• No uses el aire acondicionado</li>
                <li>• Apaga todo lo no esencial</li>
              </ul>
            </div>
          </div>

          <h2>⚡ Síntomas Tempranos (Batería Débil)</h2>
          
          <p>Estos síntomas aparecen semanas antes del fallo total:</p>

          <h3>1. Arranque Más Lento de lo Normal</h3>
          <ul>
            <li>El motor <strong>tarda más en encender</strong></li>
            <li>Se escucha que el motor de arranque <strong>va más lento</strong></li>
            <li>En días fríos cuesta más arrancar</li>
            <li>Después de varios días parado arranca con dificultad</li>
          </ul>

          <h3>2. Luces Menos Intensas</h3>
          <ul>
            <li><strong>Faros menos brillantes</strong> de lo habitual</li>
            <li>Luces interiores más <strong>amarillentas</strong></li>
            <li>Intermitentes que <strong>parpadean más lento</strong></li>
            <li>Luces de posición que <strong>se atenúan al arrancar</strong></li>
          </ul>

          <h3>3. Problemas Eléctricos Intermitentes</h3>
          <ul>
            <li>Radio que se <strong>reinicia sola</strong></li>
            <li>Reloj del coche que <strong>se resetea</strong></li>
            <li>Windows eléctricas más <strong>lentas</strong></li>
            <li>Aire acondicionado que <strong>pierde fuerza</strong></li>
          </ul>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-6 my-8">
            <div className="flex items-start">
              <Battery className="h-5 w-5 text-blue-400 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-blue-800 font-semibold mb-2">💡 Test Casero Rápido</p>
                <p className="text-blue-700 mb-0">
                  Con el motor apagado, enciende las luces largas. Si se ven amarillentas 
                  o tenues, es señal de batería débil. Una batería sana debe mantener las luces brillantes 
                  por varios minutos.
                </p>
              </div>
            </div>
          </div>

          {/* Section: Síntomas Según la Temperatura */}
          <div className="bg-gradient-to-r from-blue-50 to-orange-50 border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
              <span className="bg-gradient-to-r from-blue-500 to-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">🌡️</span>
              Síntomas Según la Temperatura
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">❄️</span>
                  En Invierno
                </h3>
                <p className="text-sm text-blue-600 mb-4">(Barcelona también tiene frío)</p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm">❄️</span>
                    <span><strong className="text-blue-800">Arranque difícil por las mañanas</strong></span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm">❄️</span>
                    <span>Necesita varios intentos para arrancar</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm">❄️</span>
                    <span>Luces muy tenues cuando hace frío</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm">❄️</span>
                    <span>Se descarga más rápido si está parado</span>
                  </li>
                </ul>
              </div>

              <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                <h3 className="text-xl font-bold text-orange-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">☀️</span>
                  En Verano
                </h3>
                <p className="text-sm text-orange-600 mb-4">(El calor también afecta)</p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="bg-orange-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm">☀️</span>
                    <span><strong className="text-orange-800">Arranques duros después de estar al sol</strong></span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-orange-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm">☀️</span>
                    <span>Problemas cuando usas mucho el A/C</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-orange-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm">☀️</span>
                    <span>Batería que se "hincha" ligeramente</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-orange-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0 text-sm">☀️</span>
                    <span>Olor a azufre cerca de la batería</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section: Tests Caseros */}
          <div className="bg-white border-2 border-purple-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
              <span className="bg-purple-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">🔍</span>
              Tests Caseros para Diagnosticar
            </h2>

            <div className="space-y-6">
              {/* Test 1 */}
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-xl font-bold text-green-800 mb-4 flex items-center">
                  <span className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">1</span>
                  Voltaje en Reposo
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-green-100">
                    <p className="text-gray-700"><strong className="text-gray-900">Con multímetro:</strong> Mide voltaje con motor parado</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center bg-white p-2 rounded-lg border border-green-100">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span className="text-sm"><strong>Bueno:</strong> 12.6V o más</span>
                    </div>
                    <div className="flex items-center bg-white p-2 rounded-lg border border-yellow-100">
                      <AlertTriangle className="h-4 w-4 text-yellow-500 mr-2" />
                      <span className="text-sm"><strong>Regular:</strong> 12.3V - 12.5V</span>
                    </div>
                    <div className="flex items-center bg-white p-2 rounded-lg border border-red-100">
                      <Battery className="h-4 w-4 text-red-500 mr-2" />
                      <span className="text-sm"><strong>Malo:</strong> Menos de 12.3V</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Test 2 */}
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center">
                  <span className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">2</span>
                  Voltaje con Motor Encendido
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-blue-100">
                    <p className="font-semibold text-gray-800 text-sm">✅ Correcto</p>
                    <p className="text-gray-600 text-sm">Debe subir a 13.5V-14.5V</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-yellow-200">
                    <p className="font-semibold text-yellow-800 text-sm">⚠️ Si no sube</p>
                    <p className="text-gray-600 text-sm">Problema de alternador</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-red-200">
                    <p className="font-semibold text-red-800 text-sm">❌ Si sube mucho</p>
                    <p className="text-gray-600 text-sm">Regulador defectuoso</p>
                  </div>
                </div>
              </div>

              {/* Test 3 */}
              <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
                <h3 className="text-xl font-bold text-yellow-800 mb-4 flex items-center">
                  <span className="bg-yellow-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm">3</span>
                  Test de Carga
                </h3>
                <div className="bg-white p-4 rounded-lg border border-yellow-100">
                  <ol className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <span className="bg-yellow-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm font-bold">1</span>
                      <span>Enciende luces largas con motor parado</span>
                    </li>
                    <li className="flex items-center">
                      <span className="bg-yellow-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm font-bold">2</span>
                      <span>Observa si el voltaje baja drásticamente</span>
                    </li>
                    <li className="flex items-center">
                      <span className="bg-yellow-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm font-bold">3</span>
                      <span>Una batería sana debe mantener <strong>&gt;12V por 5 minutos</strong></span>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Tabla de Diagnóstico */}
          <div className="bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="bg-indigo-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">📊</span>
              Tabla de Diagnóstico Rápido
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                    <th className="p-4 text-left font-bold">Síntoma</th>
                    <th className="p-4 text-left font-bold">Estado Batería</th>
                    <th className="p-4 text-left font-bold">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-green-50 border-b border-green-200">
                    <td className="p-4">Arranque normal</td>
                    <td className="p-4"><span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">✅ Buena</span></td>
                    <td className="p-4 text-gray-700">Mantenimiento regular</td>
                  </tr>
                  <tr className="bg-yellow-50 border-b border-yellow-200">
                    <td className="p-4">Arranque lento ocasional</td>
                    <td className="p-4"><span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">⚠️ Débil</span></td>
                    <td className="p-4 text-gray-700">Vigilar y revisar</td>
                  </tr>
                  <tr className="bg-orange-50 border-b border-orange-200">
                    <td className="p-4">Arranque muy difícil</td>
                    <td className="p-4"><span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">🔶 Muy débil</span></td>
                    <td className="p-4 text-gray-700">Cambio próximo</td>
                  </tr>
                  <tr className="bg-red-50">
                    <td className="p-4">No arranca</td>
                    <td className="p-4"><span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">❌ Descargada</span></td>
                    <td className="p-4 text-gray-700">Arranque de emergencia</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Section: Diferencias por Tipo de Coche */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
              <span className="bg-gray-700 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">🚗</span>
              Diferencias por Tipo de Coche
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">🚙</span>
                  Coches Antiguos (&gt;15 años)
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span>Síntomas más evidentes y graduales</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span>Menos sistemas eléctricos que falle</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                    <span>Más fácil diagnosticar</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6 border border-blue-200">
                <h3 className="text-xl font-bold text-blue-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">🚘</span>
                  Coches Modernos (&lt;5 años)
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <AlertTriangle className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" />
                    <span>Muchos sistemas eléctricos que pueden fallar</span>
                  </li>
                  <li className="flex items-center">
                    <AlertTriangle className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" />
                    <span>Errores en pantalla cuando la batería es débil</span>
                  </li>
                  <li className="flex items-center">
                    <AlertTriangle className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" />
                    <span>Sistemas Start/Stop que se desactivan solos</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Section: Cuándo Llamar */}
          <div className="bg-gradient-to-r from-red-50 to-green-50 border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
              <span className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center mr-4">⏰</span>
              Cuándo Llamar a ArrancaYa BCN
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-xl font-bold text-red-800 mb-4">🚨 Llámanos INMEDIATAMENTE si:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <span className="bg-red-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm">🚨</span>
                    <span>Tu coche no arranca para nada</span>
                  </li>
                  <li className="flex items-center">
                    <span className="bg-red-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm">🚨</span>
                    <span>Las luces apenas se ven</span>
                  </li>
                  <li className="flex items-center">
                    <span className="bg-red-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm">🚨</span>
                    <span>Escuchas solo "clic" al intentar arrancar</span>
                  </li>
                  <li className="flex items-center">
                    <span className="bg-red-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm">🚨</span>
                    <span>Tienes síntomas pero no estás seguro</span>
                  </li>
                  <li className="flex items-center">
                    <span className="bg-red-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm">🚨</span>
                    <span>Es de noche o estás en zona peligrosa</span>
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-xl font-bold text-green-800 mb-4">🔧 Programa una revisión si:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <span className="bg-green-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm">🔧</span>
                    <span>Tu batería tiene más de 3 años</span>
                  </li>
                  <li className="flex items-center">
                    <span className="bg-green-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm">🔧</span>
                    <span>Notas arranques más lentos</span>
                  </li>
                  <li className="flex items-center">
                    <span className="bg-green-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm">🔧</span>
                    <span>Los síntomas van y vienen</span>
                  </li>
                  <li className="flex items-center">
                    <span className="bg-green-200 rounded-full w-6 h-6 flex items-center justify-center mr-3 text-sm">🔧</span>
                    <span>Quieres estar seguro antes de un viaje</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-red-500 to-red-700 text-white rounded-lg p-8 mt-12">
          <div className="text-center">
            <Battery className="h-12 w-12 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">¿Reconoces Alguno de Estos Síntomas?</h2>
            <p className="text-lg mb-6 opacity-90">
              No esperes a quedarte tirado. En ArrancaYa BCN diagnosticamos y solucionamos problemas de batería 
              <strong> antes de que se conviertan en emergencias</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-3 font-bold"
              >
                <Battery className="h-5 w-5 mr-2" />
                Diagnóstico + Arranque
              </Button>
              <Button
                onClick={() => window.open('tel:+34613076099')}
                variant="outline"
                className="bg-white text-red-600 border-2 border-white hover:bg-red-50 px-8 py-3"
              >
                <Phone className="h-5 w-5 mr-2" />
                Llamar: 613 076 099
              </Button>
            </div>
            <p className="text-sm mt-4 opacity-80">
              🔋 Diagnóstico gratuito • ⚡ Arranque profesional • 🛠️ Cambio de batería in-situ
            </p>
          </div>
        </div>
      </article>
    </div>
  );
};

export default SintomasBateriaDescargada;